import React, { FC, HTMLAttributes, SyntheticEvent, useCallback, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import debounce from 'lodash/debounce';

import { ITeamDTO } from 'src/api/dtos';
import TeamsRepository from 'src/api/repositories/common/v1/teams.repository';
import { SearchBar, SearchIcon } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';

import { MultiTeamOption } from './MultiTeamOption';

interface Props {
  ids?: number[];
  onChange?: (selected: number[]) => void;
}

export const MultiTeamSelector: FC<Props> = ({ ids = [], onChange }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const {
    data: teams,
    isLoading,
    isFetching,
  } = TeamsRepository.endpoints.useSearchTeamsQuery(
    { searchTerm },
    {
      skip: !open,
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loading = isLoading || isFetching;

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (_: SyntheticEvent, value: string) => {
    debounceSearchTermChange(value);
    setInputValue(value);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTeamSelect = (team: ITeamDTO) => {
    if (ids.includes(team.id)) {
      onChange?.(ids.filter((id) => id !== team.id));
    } else {
      onChange?.([...ids, team.id]);
    }
  };

  const renderTeamOption = (_: HTMLAttributes<HTMLLIElement>, option: ITeamDTO) => {
    return <MultiTeamOption team={option} checked={ids.includes(option.id)} onSelect={handleTeamSelect} />;
  };

  return (
    <SearchBar
      // Open
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      // Options
      options={teams}
      renderOption={renderTeamOption}
      getOptionLabel={(option) => `${option.id}`}
      filterOptions={(options) => options} // Don't filter options with inputValue
      // Loading
      loading={loading}
      // Input
      inputValue={inputValue}
      onInputChange={handleInputValueChange}
      InputProps={{
        startAdornment: (
          <>
            <SearchIcon color={theme.palette.action.active} />
          </>
        ),
      }}
      // Props
      slotProps={{
        paper: {
          onMouseDown: (event) => {
            event.stopPropagation();
            event.preventDefault();
          },
          sx: {
            maxHeight: 300,
            overflow: 'hidden',
          },
        },
      }}
      // Settings
      placeholder={t('employees.searchTeams')}
      disableCloseOnSelect
    />
  );
};
